import { TbExternalLink } from 'react-icons/tb'
import { NavLink } from 'react-router-dom'

import { useDocMenu } from 'routes/Docs/components/DocsMenu/useDocMenu'
import {
  MenuItemType,
  MenuItemLinkType,
  isMenuItemLinkType,
} from 'routes/Docs/types'
import { sva, cx } from 'styled-system/css'
import { Icon } from 'ui-kit/icon'

export function FirstLevelDocsMenu() {
  const docMenu = useDocMenu()
  const firstLevelMenu: MenuItemLinkType[] = docMenu.filter((link) =>
    isMenuItemLinkType(link),
  )
  return <Menu items={firstLevelMenu} />
}

const menuRecipe = sva({
  slots: ['nav', 'ul', 'li', 'a', 'a__active', 'a__external'],
  base: {
    nav: {
      gridArea: 'menu',
      display: {
        base: 'none',
        sm: 'block',
      },
      marginInline: '-1.75rem',
      marginBlockEnd: '-1px',
      paddingInline: '4',
    },
    ul: {
      overflowX: 'auto',
      maxWidth: 'full',
      display: 'flex',
      flexWrap: 'nowrap',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '3',
      listStyle: 'none',
    },
    li: {
      color: 'fg.default',
      textWrap: 'nowrap',
    },
    a: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '1',
      borderRadius: 'l2',
      paddingInline: '4',
      paddingBlock: '2.5',
      position: 'relative',
      _before: {
        content: '""',
        display: 'block',
        position: 'absolute',
        height: '0.5',
        bottom: '0',
        left: '3.5',
        right: '3.5',
        transition: 'background-color 0.2s ease-in-out',
        backgroundColor: 'transparent',
      },
      _hover: {
        _before: {
          backgroundColor: 'fg.default',
        },
      },
    },
    a__active: {
      fontWeight: 'medium',
      _before: {
        backgroundColor: 'fg.default',
      },
    },
    a__external: {},
  },
})
const menuRecipeClassnames = menuRecipe()

function Menu({ items }: { items?: MenuItemLinkType[] }) {
  if (!items?.length) {
    return null
  }
  return (
    <nav className={menuRecipeClassnames.nav}>
      <ul className={menuRecipeClassnames.ul}>
        {items.map((item, i) => (
          <MenuItem item={item} key={i} />
        ))}
      </ul>
    </nav>
  )
}

function MenuItem({ item }: { item: MenuItemLinkType }) {
  return (
    <li className={menuRecipeClassnames.li}>
      <MenuItemLink item={item as MenuItemLinkType} />
    </li>
  )
}

function MenuItemLink({ item }: { item: MenuItemLinkType }) {
  if (item.isExternalLink) {
    return (
      <a
        className={cx(menuRecipeClassnames.a, menuRecipeClassnames.a__external)}
        href={item.to}
        rel='noreferrer'
      >
        {item.title}
        <Icon size='sm'>
          <TbExternalLink />
        </Icon>
      </a>
    )
  }
  return (
    <NavLink
      className={cx(
        menuRecipeClassnames.a,
        itemIsActive(item) && menuRecipeClassnames.a__active,
      )}
      to={item.to}
    >
      {item.title}
    </NavLink>
  )
}

function itemIsActive(item: MenuItemType) {
  return (
    item.isActive ||
    item.links?.some((child) => child.isActive || itemIsActive(child))
  )
}
